<template>
  <!-- The reader's language: English or Nederlands. With `persist` (the portal) the
       choice is saved on the user's account — it holds at every login, on every
       device and in every store. Without it (the public demo) it is remembered by
       this browser only. Until someone picks, the store's language is shown. -->
  <div class="lang-picker" role="group" :aria-label="t('language.title')">
    <template v-for="(code, i) in SUPPORTED" :key="code">
      <span v-if="i" class="sep" aria-hidden="true">·</span>
      <button
        type="button"
        class="lang-opt"
        :class="{ active: code === current }"
        :aria-pressed="code === current"
        :disabled="saving"
        @click="choose(code)"
      ><img class="flag" :src="FLAGS[code]" alt="" aria-hidden="true" />{{ t(`language.${code}`) }}</button>
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import { SUPPORTED, setLanguage } from '@/i18n'
import { useUserStore } from '@/stores/UserStore'

// One small flag per language (Benny, 2026-10-23). Images, not emoji: Windows
// shows flag emoji as the letters "GB" / "NL".
const svg = (s) => `data:image/svg+xml;utf8,${encodeURIComponent(s)}`
const FLAGS = {
  en: svg('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30">'
    + '<clipPath id="t"><path d="M30,15h30v15zv15h-30zh-30v-15zv-15h30z"/></clipPath>'
    + '<path d="M0,0v30h60v-30z" fill="#012169"/>'
    + '<path d="M0,0L60,30M60,0L0,30" stroke="#fff" stroke-width="6"/>'
    + '<path d="M0,0L60,30M60,0L0,30" clip-path="url(#t)" stroke="#C8102E" stroke-width="4"/>'
    + '<path d="M30,0v30M0,15h60" stroke="#fff" stroke-width="10"/>'
    + '<path d="M30,0v30M0,15h60" stroke="#C8102E" stroke-width="6"/></svg>'),
  nl: svg('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 6">'
    + '<rect width="9" height="6" fill="#21468B"/><rect width="9" height="4" fill="#fff"/>'
    + '<rect width="9" height="2" fill="#AE1C28"/></svg>'),
}

const props = defineProps({
  // true: save on the account (logged-in portal); false: this browser only (demo)
  persist: { type: Boolean, default: true },
})

const { t, locale } = useI18n()
const toast = useToast()
const saving = ref(false)
const current = computed(() => locale.value)

async function choose(code) {
  if (code === current.value || saving.value) return
  if (!props.persist) {
    setLanguage(code)
    return
  }
  saving.value = true
  try {
    await useUserStore().setMyLanguage(code)
  } catch (e) {
    toast.error(t('language.saveFailed'))
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.lang-picker {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 0;
  color: #425461;
  font-size: 0.78rem;
}
/* Round flag with a ring: white inner edge, then a soft grey ring — blue on the
   language in use, so the choice reads from the flag alone. */
.flag {
  width: 16px;
  height: 16px;
  margin-right: 6px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid #fff;
  box-shadow: 0 0 0 1px #c9d3dc;
  transition: box-shadow 0.15s ease;
}
.lang-opt.active .flag {
  box-shadow: 0 0 0 1.5px #4f7eb3;
}
.lang-opt:hover:not(.active):not(:disabled) .flag {
  box-shadow: 0 0 0 1px #4f7eb3;
}
.sep {
  color: #9aa8b3;
  margin: 0 2px;
}
.lang-opt {
  display: inline-flex;
  align-items: center;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  line-height: 1.2;
  color: #4f7eb3;
  cursor: pointer;
}
.lang-opt:hover:not(.active):not(:disabled) {
  text-decoration: underline;
}
.lang-opt.active {
  color: #425461;
  font-weight: 700;
  cursor: default;
}
.lang-opt:disabled:not(.active) {
  opacity: 0.6;
  cursor: progress;
}
</style>
